import { db } from '../lib/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, limit, getDoc, doc } from 'firebase/firestore';
import { formatDate } from '../utils/dateFormatters';

// AI COMPLETELY DISABLED - ALL API KEYS REMOVED
const AI_CONFIG = {
  openai: {
    apiKey: null, // Using Firebase Functions for AI instead
    model: 'DISABLED',
    visionModel: 'DISABLED'
  },
  claude: {
    apiKey: null, // DISABLED: was process.env.REACT_APP_ANTHROPIC_API_KEY,
    model: 'DISABLED'
  }
};

class AIService {
  constructor() {
    this.initialized = false;
    this.cache = new Map();
  }

  // Initialize AI service
  async initialize() {
    if (this.initialized) return;
    
    // Check API keys
    if (!AI_CONFIG.openai.apiKey && !AI_CONFIG.claude.apiKey) {
      console.warn('AI Service: No API keys configured. AI features will be limited.');
    }
    
    this.initialized = true;
  }

  // ============================================
  // 1. SMART PRODUCT TAXONOMY SUGGESTIONS
  // ============================================

  async suggestProductCategories(description) {
    try {
      const prompt = `Analyze this project description and suggest the most appropriate product categories from furniture design:

Description: "${description}"

Based on this description, identify:
1. Primary product type (chair, sofa, table, etc.)
2. Style category (modern, traditional, industrial, etc.)
3. Key features mentioned
4. Materials referenced
5. Approximate dimensions if mentioned

Return as JSON with keys: productType, style, features, materials, dimensions`;

      const response = await this.callOpenAI(prompt);
      return JSON.parse(response);
    } catch (error) {
      console.error('Error suggesting product categories:', error);
      return null;
    }
  }

  async enhanceProductDescription(quoteData) {
    try {
      const prompt = `Create a professional product description for an interior design quote based on this structured data:

Product: ${quoteData.product?.label || 'Custom piece'}
Style: ${quoteData.style?.label || 'Not specified'}
Materials: ${quoteData.materials.join(', ') || 'Not specified'}
Features: ${quoteData.features.join(', ') || 'None'}
Dimensions: ${Object.entries(quoteData.dimensions).map(([k,v]) => `${k}: ${v}`).join(', ') || 'Custom sizing'}

Write a professional, detailed description suitable for:
1. Customer quote/proposal
2. Supplier specifications
3. Project documentation

Include estimated complexity, quality expectations, and any special considerations.
Keep it professional but with the Pink Flamingo Interiors brand voice (fun and fabulous without the fuss).`;

      const response = await this.callOpenAI(prompt);
      return response;
    } catch (error) {
      console.error('Error enhancing product description:', error);
      return null;
    }
  }

  async suggestPricingFactors(quoteData) {
    try {
      const prompt = `Analyze this product specification and identify key pricing factors:

Product: ${quoteData.product?.label}
Style: ${quoteData.style?.label}
Materials: ${quoteData.materials.join(', ')}
Features: ${quoteData.features.join(', ')}
Dimensions: ${Object.entries(quoteData.dimensions).map(([k,v]) => `${k}: ${v}`).join(', ')}

Identify pricing factors in order of impact:
1. Material costs and quality
2. Manufacturing complexity
3. Size/scale impact
4. Special features costs
5. Labor intensity
6. Timeline considerations

Return as JSON with factors array containing: factor, impact (high/medium/low), reasoning`;

      const response = await this.callOpenAI(prompt);
      return JSON.parse(response);
    } catch (error) {
      console.error('Error suggesting pricing factors:', error);
      return null;
    }
  }

  // ============================================
  // 2. SMART QUOTE ANALYSIS & RECOMMENDATIONS
  // ============================================

  async analyzeQuotes(projectId, taskId) {
    try {
      // Fetch all quotes for the task
      const quotesSnapshot = await getDocs(
        query(collection(db, 'projects', projectId, 'tasks', taskId, 'quotes'),
        where('status', '==', 'submitted'))
      );
      
      const quotes = quotesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (quotes.length === 0) return null;

      // Fetch supplier history for reliability scoring
      const supplierScores = await this.calculateSupplierScores(quotes);
      
      // Analyze quotes
      const analysis = {
        priceAnalysis: this.analyzePricing(quotes),
        timelineAnalysis: this.analyzeTimelines(quotes),
        reliabilityScores: supplierScores,
        recommendations: [],
        warnings: [],
        insights: []
      };

      // Generate recommendations
      analysis.recommendations = this.generateQuoteRecommendations(quotes, analysis);
      
      // Detect anomalies
      analysis.warnings = this.detectQuoteAnomalies(quotes, analysis);
      
      // Generate insights
      analysis.insights = this.generateQuoteInsights(quotes, analysis);

      // Store analysis
      await addDoc(collection(db, 'aiAnalysis'), {
        type: 'quote_analysis',
        projectId,
        taskId,
        analysis,
        createdAt: new Date()
      });

      return analysis;
    } catch (error) {
      console.error('Error analyzing quotes:', error);
      return null;
    }
  }

  analyzePricing(quotes) {
    const amounts = quotes.map(q => q.amount).filter(a => a);
    if (amounts.length === 0) return null;

    const sorted = [...amounts].sort((a, b) => a - b);
    const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
    const median = sorted[Math.floor(sorted.length / 2)];
    const stdDev = Math.sqrt(
      amounts.reduce((sq, n) => sq + Math.pow(n - avg, 2), 0) / amounts.length
    );

    return {
      average: avg,
      median: median,
      lowest: sorted[0],
      highest: sorted[sorted.length - 1],
      standardDeviation: stdDev,
      spread: sorted[sorted.length - 1] - sorted[0],
      outliers: amounts.filter(a => Math.abs(a - avg) > 2 * stdDev)
    };
  }

  analyzeTimelines(quotes) {
    const timelines = quotes.map(q => {
      // Parse timeline strings like "2-3 weeks" or "14 days"
      const match = q.timeline?.match(/(\d+)(?:-(\d+))?\s*(days?|weeks?|months?)/i);
      if (!match) return null;
      
      let days = parseInt(match[1]);
      const unit = match[3].toLowerCase();
      
      if (unit.startsWith('week')) days *= 7;
      if (unit.startsWith('month')) days *= 30;
      
      if (match[2]) {
        let maxDays = parseInt(match[2]);
        if (unit.startsWith('week')) maxDays *= 7;
        if (unit.startsWith('month')) maxDays *= 30;
        days = (days + maxDays) / 2; // Use average
      }
      
      return days;
    }).filter(t => t);

    if (timelines.length === 0) return null;

    const sorted = [...timelines].sort((a, b) => a - b);
    const avg = timelines.reduce((a, b) => a + b, 0) / timelines.length;

    return {
      averageDays: avg,
      fastestDays: sorted[0],
      slowestDays: sorted[sorted.length - 1],
      formatted: {
        average: `${Math.round(avg)} days`,
        fastest: `${sorted[0]} days`,
        slowest: `${sorted[sorted.length - 1]} days`
      }
    };
  }

  async calculateSupplierScores(quotes) {
    const scores = {};
    
    for (const quote of quotes) {
      // Fetch supplier's quote history
      const historySnapshot = await getDocs(
        query(
          collection(db, 'quoteHistory'),
          where('supplierId', '==', quote.supplierId),
          orderBy('completedAt', 'desc'),
          limit(10)
        )
      );
      
      const history = historySnapshot.docs.map(doc => doc.data());
      
      // Calculate reliability score
      const onTimeDeliveries = history.filter(h => h.deliveredOnTime).length;
      const totalDeliveries = history.filter(h => h.completed).length;
      const acceptanceRate = history.filter(h => h.accepted).length / history.length;
      
      scores[quote.supplierId] = {
        supplierId: quote.supplierId,
        supplierName: quote.supplierName,
        reliabilityScore: totalDeliveries > 0 ? (onTimeDeliveries / totalDeliveries) * 100 : 50,
        acceptanceRate: acceptanceRate * 100,
        completedProjects: totalDeliveries,
        averageRating: history.reduce((sum, h) => sum + (h.rating || 0), 0) / history.length || 0
      };
    }
    
    return scores;
  }

  generateQuoteRecommendations(quotes, analysis) {
    const recommendations = [];
    
    // Price-based recommendation
    if (analysis.priceAnalysis) {
      const bestValue = quotes.find(q => 
        q.amount <= analysis.priceAnalysis.median &&
        analysis.reliabilityScores[q.supplierId]?.reliabilityScore > 70
      );
      
      if (bestValue) {
        recommendations.push({
          type: 'best_value',
          supplierId: bestValue.supplierId,
          supplierName: bestValue.supplierName,
          reason: 'Best combination of price and reliability'
        });
      }
    }
    
    // Timeline-based recommendation
    if (analysis.timelineAnalysis) {
      const fastest = quotes.sort((a, b) => {
        const aDays = this.parseTimelineDays(a.timeline);
        const bDays = this.parseTimelineDays(b.timeline);
        return aDays - bDays;
      })[0];
      
      if (fastest) {
        recommendations.push({
          type: 'fastest_delivery',
          supplierId: fastest.supplierId,
          supplierName: fastest.supplierName,
          reason: `Fastest delivery: ${fastest.timeline}`
        });
      }
    }
    
    // Reliability-based recommendation
    const mostReliable = Object.values(analysis.reliabilityScores)
      .sort((a, b) => b.reliabilityScore - a.reliabilityScore)[0];
    
    if (mostReliable) {
      recommendations.push({
        type: 'most_reliable',
        supplierId: mostReliable.supplierId,
        supplierName: mostReliable.supplierName,
        reason: `Highest reliability score: ${mostReliable.reliabilityScore.toFixed(0)}%`
      });
    }
    
    return recommendations;
  }

  detectQuoteAnomalies(quotes, analysis) {
    const warnings = [];
    
    if (analysis.priceAnalysis) {
      // Check for outliers
      analysis.priceAnalysis.outliers.forEach(amount => {
        const quote = quotes.find(q => q.amount === amount);
        if (quote) {
          const deviation = ((amount - analysis.priceAnalysis.average) / analysis.priceAnalysis.average * 100).toFixed(0);
          warnings.push({
            type: 'price_outlier',
            supplierId: quote.supplierId,
            supplierName: quote.supplierName,
            message: `Quote is ${Math.abs(deviation)}% ${deviation > 0 ? 'above' : 'below'} average`,
            severity: Math.abs(deviation) > 50 ? 'high' : 'medium'
          });
        }
      });
      
      // Check for suspiciously low quotes
      const tooLow = quotes.filter(q => q.amount < analysis.priceAnalysis.average * 0.5);
      tooLow.forEach(quote => {
        warnings.push({
          type: 'suspiciously_low',
          supplierId: quote.supplierId,
          supplierName: quote.supplierName,
          message: 'Quote seems unusually low - verify scope understanding',
          severity: 'high'
        });
      });
    }
    
    // Check for vague timelines
    const vagueTimelines = quotes.filter(q => 
      !q.timeline || q.timeline.toLowerCase().includes('asap') || q.timeline.toLowerCase().includes('tbd')
    );
    
    vagueTimelines.forEach(quote => {
      warnings.push({
        type: 'vague_timeline',
        supplierId: quote.supplierId,
        supplierName: quote.supplierName,
        message: 'Timeline is not specific',
        severity: 'medium'
      });
    });
    
    return warnings;
  }

  generateQuoteInsights(quotes, analysis) {
    const insights = [];
    
    // Price spread insight
    if (analysis.priceAnalysis && analysis.priceAnalysis.spread > analysis.priceAnalysis.average * 0.3) {
      insights.push({
        type: 'high_price_variance',
        message: 'There\'s significant price variation between quotes. Consider clarifying requirements.',
        importance: 'high'
      });
    }
    
    // Competition insight
    if (quotes.length >= 3) {
      insights.push({
        type: 'good_competition',
        message: `You have ${quotes.length} competitive quotes to choose from.`,
        importance: 'medium'
      });
    }
    
    // Timeline insight
    if (analysis.timelineAnalysis) {
      const avgWeeks = Math.ceil(analysis.timelineAnalysis.averageDays / 7);
      insights.push({
        type: 'timeline_expectation',
        message: `Average completion time is ${avgWeeks} weeks. Plan accordingly.`,
        importance: 'medium'
      });
    }
    
    return insights;
  }

  parseTimelineDays(timeline) {
    if (!timeline) return 999;
    const match = timeline.match(/(\d+)/);
    return match ? parseInt(match[1]) : 999;
  }

  // ============================================
  // 2. AUTOMATED PROJECT INTELLIGENCE
  // ============================================

  async predictTaskDuration(taskCategory, taskDescription) {
    try {
      // Fetch historical data for similar tasks
      const historySnapshot = await getDocs(
        query(
          collection(db, 'taskHistory'),
          where('category', '==', taskCategory),
          orderBy('completedAt', 'desc'),
          limit(20)
        )
      );
      
      const history = historySnapshot.docs.map(doc => doc.data());
      
      if (history.length === 0) {
        // Default predictions by category
        const defaults = {
          'upholstery': 14,
          'delivery': 3,
          'fabric': 7,
          'furniture': 21,
          'installation': 5,
          'design': 10,
          'consultation': 2,
          'other': 7
        };
        return defaults[taskCategory] || 7;
      }
      
      // Calculate average duration
      const durations = history.map(h => h.actualDuration || h.estimatedDays);
      const avgDuration = durations.reduce((a, b) => a + b, 0) / durations.length;
      
      // Adjust based on complexity indicators in description
      let adjustment = 1;
      if (taskDescription) {
        if (taskDescription.toLowerCase().includes('custom')) adjustment *= 1.2;
        if (taskDescription.toLowerCase().includes('urgent')) adjustment *= 0.8;
        if (taskDescription.toLowerCase().includes('complex')) adjustment *= 1.3;
        if (taskDescription.toLowerCase().includes('simple')) adjustment *= 0.7;
      }
      
      return Math.round(avgDuration * adjustment);
    } catch (error) {
      console.error('Error predicting task duration:', error);
      return 7; // Default fallback
    }
  }

  async forecastProjectBudget(projectData) {
    try {
      const { tasks, designStyle, rooms } = projectData;
      
      // Base estimates by task category
      const categoryEstimates = {
        'upholstery': 2500,
        'delivery': 200,
        'fabric': 1500,
        'furniture': 3500,
        'installation': 500,
        'design': 1000,
        'consultation': 300,
        'other': 500
      };
      
      // Style multipliers
      const styleMultipliers = {
        'modern': 1.0,
        'luxury': 1.5,
        'contemporary': 1.1,
        'traditional': 1.2,
        'minimalist': 0.9,
        'eclectic': 1.3
      };
      
      // Calculate base budget
      let baseBudget = 0;
      tasks.forEach(task => {
        baseBudget += categoryEstimates[task.category] || 500;
      });
      
      // Apply style multiplier
      const styleMultiplier = styleMultipliers[designStyle] || 1.0;
      
      // Apply room count multiplier
      const roomMultiplier = rooms ? 1 + (rooms.length - 1) * 0.15 : 1;
      
      const estimatedBudget = baseBudget * styleMultiplier * roomMultiplier;
      
      return {
        estimated: Math.round(estimatedBudget),
        breakdown: {
          base: baseBudget,
          styleAdjustment: baseBudget * (styleMultiplier - 1),
          roomAdjustment: baseBudget * (roomMultiplier - 1)
        },
        confidence: 'medium',
        range: {
          min: Math.round(estimatedBudget * 0.8),
          max: Math.round(estimatedBudget * 1.3)
        }
      };
    } catch (error) {
      console.error('Error forecasting budget:', error);
      return null;
    }
  }

  async detectProjectRisks(projectId) {
    try {
      const risks = [];
      
      // Fetch project data
      const projectDoc = await getDoc(doc(db, 'projects', projectId));
      const project = projectDoc.data();
      
      // Check timeline risks
      const daysUntilDue = (project.expectedEndDate?.toDate() - new Date()) / (1000 * 60 * 60 * 24);
      if (daysUntilDue < 7) {
        risks.push({
          type: 'timeline_pressure',
          severity: 'high',
          message: 'Project deadline is very close',
          mitigation: 'Consider expedited shipping or additional resources'
        });
      }
      
      // Check supplier risks
      const tasksSnapshot = await getDocs(collection(db, 'projects', projectId, 'tasks'));
      const unassignedTasks = tasksSnapshot.docs.filter(doc => 
        !doc.data().assignedSuppliers || doc.data().assignedSuppliers.length === 0
      );
      
      if (unassignedTasks.length > 0) {
        risks.push({
          type: 'unassigned_tasks',
          severity: 'medium',
          message: `${unassignedTasks.length} tasks have no assigned suppliers`,
          mitigation: 'Assign suppliers to ensure timely quotes'
        });
      }
      
      // Check budget risks
      const quotesSnapshot = await getDocs(
        query(collection(db, 'projects', projectId, 'quotes'))
      );
      const totalQuoted = quotesSnapshot.docs.reduce((sum, doc) => 
        sum + (doc.data().amount || 0), 0
      );
      
      if (project.budget && totalQuoted > project.budget * 1.1) {
        risks.push({
          type: 'budget_overrun',
          severity: 'high',
          message: `Quotes exceed budget by ${((totalQuoted / project.budget - 1) * 100).toFixed(0)}%`,
          mitigation: 'Negotiate with suppliers or adjust project scope'
        });
      }
      
      return risks;
    } catch (error) {
      console.error('Error detecting project risks:', error);
      return [];
    }
  }

  // ============================================
  // 4. COMMUNICATION ENHANCEMENT
  // ============================================

  async generateEmail(type, context) {
    const templates = {
      quote_request: {
        subject: `Quote Request: ${context.taskName} - ${context.projectName}`,
        body: `Dear ${context.supplierName},

We would like to request a quote for ${context.taskName} as part of our ${context.projectName} project.

Project Details:
- Task: ${context.taskName}
- Category: ${context.taskCategory}
- Timeline: ${context.timeline || 'To be discussed'}
- Special Requirements: ${context.requirements || 'See attached specifications'}

Please provide:
1. Detailed cost breakdown
2. Estimated timeline
3. Materials to be used
4. Any relevant terms and conditions

We look forward to receiving your quote by ${context.dueDate || 'end of this week'}.

Best regards,
Pink Flamingo Interiors`
      },
      
      follow_up: {
        subject: `Follow-up: ${context.subject}`,
        body: `Dear ${context.recipientName},

I hope this email finds you well. I wanted to follow up on ${context.topic}.

${context.customMessage || 'We would appreciate an update at your earliest convenience.'}

Please let me know if you need any additional information.

Best regards,
Pink Flamingo Interiors`
      },
      
      quote_acceptance: {
        subject: `Quote Accepted: ${context.taskName}`,
        body: `Dear ${context.supplierName},

We are pleased to inform you that your quote for ${context.taskName} has been accepted.

Quote Details:
- Amount: ${context.amount}
- Timeline: ${context.timeline}
- Start Date: ${context.startDate}

Next Steps:
1. Please confirm your availability for the proposed start date
2. Send through any required documentation
3. Coordinate with our project manager for site access

We look forward to working with you.

Best regards,
Pink Flamingo Interiors`
      }
    };
    
    return templates[type] || null;
  }

  async analyzeSentiment(text) {
    // Simple sentiment analysis
    const positiveWords = ['excellent', 'great', 'wonderful', 'perfect', 'amazing', 'love', 'fantastic', 'beautiful'];
    const negativeWords = ['problem', 'issue', 'unhappy', 'disappointed', 'terrible', 'bad', 'wrong', 'delay', 'late'];
    const urgentWords = ['urgent', 'asap', 'immediately', 'critical', 'emergency'];
    
    const lowerText = text.toLowerCase();
    
    const positiveCount = positiveWords.filter(word => lowerText.includes(word)).length;
    const negativeCount = negativeWords.filter(word => lowerText.includes(word)).length;
    const urgentCount = urgentWords.filter(word => lowerText.includes(word)).length;
    
    return {
      sentiment: positiveCount > negativeCount ? 'positive' : negativeCount > positiveCount ? 'negative' : 'neutral',
      urgency: urgentCount > 0 ? 'high' : 'normal',
      scores: {
        positive: positiveCount,
        negative: negativeCount,
        urgent: urgentCount
      }
    };
  }

  async prioritizeNotifications(notifications) {
    const prioritized = notifications.map(notif => {
      let priority = 0;
      
      // Time-based priority
      const age = (new Date() - notif.createdAt) / (1000 * 60 * 60); // hours
      if (age > 48) priority += 3;
      else if (age > 24) priority += 2;
      else if (age > 12) priority += 1;
      
      // Type-based priority
      if (notif.type === 'quote-submitted') priority += 5;
      if (notif.type === 'payment-received') priority += 4;
      if (notif.type === 'issue-reported') priority += 6;
      if (notif.type === 'info-request') priority += 3;
      
      // Amount-based priority
      if (notif.amount && notif.amount > 5000) priority += 2;
      
      return { ...notif, priority };
    });
    
    return prioritized.sort((a, b) => b.priority - a.priority);
  }

  // ============================================
  // 5. VISUAL AI FEATURES
  // ============================================

  async analyzeProjectPhoto(photoUrl, photoType = 'general') {
    // This would integrate with a vision API
    // For now, return mock analysis
    return {
      detected: {
        roomType: 'living room',
        furniture: ['sofa', 'coffee table', 'armchair'],
        style: 'contemporary',
        colors: ['beige', 'gray', 'brown'],
        condition: 'good'
      },
      suggestions: [
        'Consider reupholstering the sofa in a complementary fabric',
        'Add accent pillows for color contrast',
        'Update coffee table finish to match new design'
      ],
      estimatedWork: {
        upholstery: true,
        painting: false,
        carpentry: false,
        decoration: true
      }
    };
  }

  async compareProgressPhotos(beforeUrl, afterUrl) {
    // This would use computer vision to compare photos
    // For now, return mock comparison
    return {
      completionPercentage: 85,
      changesDetected: [
        'Sofa has been reupholstered',
        'New curtains installed',
        'Walls repainted'
      ],
      remainingWork: [
        'Install new light fixtures',
        'Place decorative elements'
      ],
      quality: {
        score: 9,
        notes: 'Excellent workmanship on upholstery'
      }
    };
  }

  async suggestSimilarProjects(photoUrl) {
    // Would use image similarity matching
    // Mock implementation
    return {
      similarProjects: [
        {
          id: 'proj_123',
          name: 'Paddington Residence',
          similarity: 0.89,
          budget: 12000,
          duration: '3 weeks'
        },
        {
          id: 'proj_124',
          name: 'Bondi Beach House',
          similarity: 0.76,
          budget: 15000,
          duration: '4 weeks'
        }
      ],
      styleMatch: 'contemporary coastal',
      suggestedSuppliers: ['ABC Upholstery', 'XYZ Fabrics']
    };
  }

  // ============================================
  // 6. PREDICTIVE ANALYTICS
  // ============================================

  async predictSupplierPerformance(supplierId, taskType) {
    try {
      // Fetch supplier's historical performance
      const historySnapshot = await getDocs(
        query(
          collection(db, 'supplierPerformance'),
          where('supplierId', '==', supplierId),
          orderBy('completedAt', 'desc'),
          limit(10)
        )
      );
      
      const history = historySnapshot.docs.map(doc => doc.data());
      
      if (history.length === 0) {
        return {
          predictedOnTime: null, // No data available
          confidence: 'none',
          factors: ['No historical data available - complete some projects first']
        };
      }
      
      // Calculate metrics
      const onTimeCount = history.filter(h => h.deliveredOnTime).length;
      const avgDelayDays = history
        .filter(h => !h.deliveredOnTime)
        .reduce((sum, h) => sum + (h.delayDays || 0), 0) / history.length;
      
      const taskTypeHistory = history.filter(h => h.taskType === taskType);
      const taskTypePerformance = taskTypeHistory.length > 0
        ? (taskTypeHistory.filter(h => h.deliveredOnTime).length / taskTypeHistory.length) * 100
        : null;
      
      // Calculate prediction
      const baseScore = (onTimeCount / history.length) * 100;
      const taskScore = taskTypePerformance || baseScore;
      const finalScore = (baseScore * 0.6 + taskScore * 0.4);
      
      return {
        predictedOnTime: Math.round(finalScore),
        confidence: history.length >= 5 ? 'high' : 'medium',
        factors: [
          `Historical on-time rate: ${Math.round(baseScore)}%`,
          taskTypePerformance ? `${taskType} success rate: ${Math.round(taskTypePerformance)}%` : null,
          avgDelayDays > 0 ? `Average delay when late: ${avgDelayDays.toFixed(1)} days` : null
        ].filter(f => f),
        recommendation: finalScore > 80 ? 'Highly reliable' : finalScore > 60 ? 'Generally reliable' : 'Consider backup options'
      };
    } catch (error) {
      console.error('Error predicting supplier performance:', error);
      return null;
    }
  }

  async calculateClientLifetimeValue(clientId) {
    try {
      const projectsSnapshot = await getDocs(
        query(
          collection(db, 'projects'),
          where('clientId', '==', clientId)
        )
      );
      
      const projects = projectsSnapshot.docs.map(doc => doc.data());
      
      // Calculate metrics
      const totalRevenue = projects.reduce((sum, p) => sum + (p.totalRevenue || 0), 0);
      const avgProjectValue = totalRevenue / projects.length;
      const projectFrequency = projects.length / ((new Date() - projects[0].createdAt) / (365 * 24 * 60 * 60 * 1000));
      
      // Predict future value (simple model)
      const predictedAnnualProjects = projectFrequency;
      const predictedAnnualValue = predictedAnnualProjects * avgProjectValue;
      const predictedLifetimeValue = predictedAnnualValue * 5; // 5-year projection
      
      return {
        historicalValue: totalRevenue,
        avgProjectValue: avgProjectValue,
        projectCount: projects.length,
        predictedAnnualValue: Math.round(predictedAnnualValue),
        predictedLifetimeValue: Math.round(predictedLifetimeValue),
        customerSegment: predictedLifetimeValue > 100000 ? 'VIP' : predictedLifetimeValue > 50000 ? 'Premium' : 'Standard'
      };
    } catch (error) {
      console.error('Error calculating client lifetime value:', error);
      return null;
    }
  }

  async predictSeasonalTrends() {
    try {
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      const projects = projectsSnapshot.docs.map(doc => doc.data());
      
      // Group by month
      const monthlyData = {};
      projects.forEach(project => {
        const month = project.createdAt.toDate().getMonth();
        monthlyData[month] = (monthlyData[month] || 0) + 1;
      });
      
      // Identify patterns
      const busyMonths = Object.entries(monthlyData)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([month]) => parseInt(month));
      
      const quietMonths = Object.entries(monthlyData)
        .sort(([,a], [,b]) => a - b)
        .slice(0, 3)
        .map(([month]) => parseInt(month));
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      return {
        busyPeriods: busyMonths.map(m => monthNames[m]),
        quietPeriods: quietMonths.map(m => monthNames[m]),
        currentMonth: monthNames[new Date().getMonth()],
        prediction: busyMonths.includes(new Date().getMonth()) ? 'Expect high demand' : 'Normal demand expected',
        recommendation: busyMonths.includes(new Date().getMonth() + 1) ? 'Prepare for busy period next month' : null
      };
    } catch (error) {
      console.error('Error predicting seasonal trends:', error);
      return null;
    }
  }

  // ============================================
  // 7. PROCESS AUTOMATION
  // ============================================

  async generateContract(projectData, supplierData, taskData) {
    const template = `SERVICE AGREEMENT

This Agreement is entered into as of ${formatDate(new Date())} between:

CLIENT: Pink Flamingo Interiors
ABN: [Your ABN]
Address: [Your Address]

SUPPLIER: ${supplierData.name}
ABN: ${supplierData.abn || '[To be provided]'}
Address: ${supplierData.address || '[To be provided]'}

PROJECT DETAILS:
Project Name: ${projectData.name}
Task: ${taskData.name}
Category: ${taskData.category}

SCOPE OF WORK:
${taskData.description}

DELIVERABLES:
- ${taskData.requirements?.join('\n- ') || 'As discussed'}

TIMELINE:
Commencement Date: ${taskData.startDate || '[To be confirmed]'}
Completion Date: ${taskData.dueDate || '[To be confirmed]'}
Duration: ${taskData.estimatedDays} days

PAYMENT TERMS:
Total Amount: $${taskData.acceptedQuote?.amount || '[To be confirmed]'}
Payment Schedule:
- 30% deposit upon signing
- 40% at milestone completion
- 30% upon final delivery

TERMS AND CONDITIONS:
1. Quality Standards: All work must meet industry standards
2. Warranties: 12-month warranty on workmanship
3. Insurance: Supplier maintains appropriate insurance
4. Variations: Any changes must be agreed in writing
5. Termination: Either party may terminate with 14 days notice

SIGNATURES:
Pink Flamingo Interiors: ___________________ Date: ___________
${supplierData.name}: ___________________ Date: ___________`;

    return {
      content: template,
      filename: `Contract_${projectData.name}_${taskData.name}_${Date.now()}.txt`,
      type: 'contract'
    };
  }

  async generateInvoice(projectData, items) {
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const gst = subtotal * 0.1;
    const total = subtotal + gst;
    
    const template = `INVOICE

Invoice Number: INV-${Date.now().toString().slice(-6)}
Date: ${formatDate(new Date())}

FROM:
Pink Flamingo Interiors
ABN: [Your ABN]
[Your Address]
Email: hello@pinkflamingointeriors.com.au

TO:
${projectData.clientName}
${projectData.clientAddress || ''}
${projectData.clientEmail || ''}

PROJECT: ${projectData.name}

ITEMS:
${items.map(item => `${item.description.padEnd(40)} $${item.amount.toFixed(2)}`).join('\n')}

Subtotal: $${subtotal.toFixed(2)}
GST (10%): $${gst.toFixed(2)}
TOTAL: $${total.toFixed(2)}

Payment Terms: Due within 30 days
Payment Methods: Bank transfer or credit card

Thank you for your business!`;

    return {
      content: template,
      filename: `Invoice_${projectData.name}_${Date.now()}.txt`,
      type: 'invoice',
      amount: total
    };
  }

  async optimizeSchedule(tasks, suppliers) {
    // Simple scheduling optimization
    const schedule = [];
    const supplierAvailability = {};
    
    // Sort tasks by priority and dependencies
    const sortedTasks = [...tasks].sort((a, b) => {
      if (a.priority === 'high' && b.priority !== 'high') return -1;
      if (b.priority === 'high' && a.priority !== 'high') return 1;
      return 0;
    });
    
    sortedTasks.forEach(task => {
      const assignedSupplier = task.assignedSuppliers?.[0];
      if (!assignedSupplier) return;
      
      // Find next available slot for supplier
      const supplierNextAvailable = supplierAvailability[assignedSupplier] || new Date();
      const taskStart = new Date(Math.max(supplierNextAvailable, new Date()));
      const taskEnd = new Date(taskStart);
      taskEnd.setDate(taskEnd.getDate() + task.estimatedDays);
      
      schedule.push({
        taskId: task.id,
        taskName: task.name,
        supplierId: assignedSupplier,
        startDate: taskStart,
        endDate: taskEnd,
        duration: task.estimatedDays
      });
      
      supplierAvailability[assignedSupplier] = taskEnd;
    });
    
    return {
      schedule,
      totalDuration: Math.max(...Object.values(supplierAvailability).map(d => d - new Date())) / (1000 * 60 * 60 * 24),
      parallelTasks: schedule.filter(s => s.startDate.getTime() === new Date().getTime()).length
    };
  }

  // ============================================
  // 8. QUALITY CONTROL
  // ============================================

  async detectAnomalies(data, type) {
    const anomalies = [];
    
    if (type === 'quotes') {
      // Already implemented in quote analysis
      return this.detectQuoteAnomalies(data, null);
    }
    
    if (type === 'expenses') {
      const amounts = data.map(e => e.amount);
      const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
      const stdDev = Math.sqrt(amounts.reduce((sq, n) => sq + Math.pow(n - avg, 2), 0) / amounts.length);
      
      data.forEach(expense => {
        if (Math.abs(expense.amount - avg) > 2 * stdDev) {
          anomalies.push({
            type: 'unusual_expense',
            item: expense.description,
            amount: expense.amount,
            deviation: `${((expense.amount - avg) / avg * 100).toFixed(0)}% from average`,
            severity: Math.abs(expense.amount - avg) > 3 * stdDev ? 'high' : 'medium'
          });
        }
      });
    }
    
    return anomalies;
  }

  async checkCompliance(projectId) {
    const issues = [];
    
    try {
      // Check for required documentation
      const docsSnapshot = await getDocs(
        collection(db, 'projects', projectId, 'documents')
      );
      
      const requiredDocs = ['contract', 'insurance', 'safety_plan'];
      const existingDocs = docsSnapshot.docs.map(doc => doc.data().type);
      
      requiredDocs.forEach(docType => {
        if (!existingDocs.includes(docType)) {
          issues.push({
            type: 'missing_document',
            document: docType,
            severity: 'medium',
            action: `Upload ${docType.replace('_', ' ')}`
          });
        }
      });
      
      // Check for approvals
      const tasksSnapshot = await getDocs(
        collection(db, 'projects', projectId, 'tasks')
      );
      
      const unapprovedTasks = tasksSnapshot.docs.filter(doc => 
        doc.data().status === 'completed' && !doc.data().clientApproved
      );
      
      if (unapprovedTasks.length > 0) {
        issues.push({
          type: 'pending_approvals',
          count: unapprovedTasks.length,
          severity: 'low',
          action: 'Get client approval for completed tasks'
        });
      }
      
      return {
        compliant: issues.length === 0,
        issues,
        score: Math.max(0, 100 - (issues.length * 10))
      };
    } catch (error) {
      console.error('Error checking compliance:', error);
      return { compliant: false, issues: [], score: 0 };
    }
  }

  async assessProjectQuality(projectId) {
    try {
      const metrics = {
        onTimeDelivery: 0,
        budgetAdherence: 0,
        clientSatisfaction: 0,
        supplierPerformance: 0,
        documentationComplete: 0
      };
      
      // Fetch project data
      const projectDoc = await getDoc(doc(db, 'projects', projectId));
      const project = projectDoc.data();
      
      // On-time delivery
      if (project.completedAt && project.expectedEndDate) {
        const delayDays = (project.completedAt - project.expectedEndDate) / (1000 * 60 * 60 * 24);
        metrics.onTimeDelivery = Math.max(0, 100 - (delayDays * 5));
      }
      
      // Budget adherence
      if (project.budget && project.actualCost) {
        const overrun = ((project.actualCost - project.budget) / project.budget) * 100;
        metrics.budgetAdherence = Math.max(0, 100 - overrun);
      }
      
      // Client satisfaction (from feedback) - only show if we have actual rating
      metrics.clientSatisfaction = project.clientRating ? project.clientRating * 20 : null;
      
      // Supplier performance
      const tasksSnapshot = await getDocs(
        collection(db, 'projects', projectId, 'tasks')
      );
      const completedTasks = tasksSnapshot.docs.filter(doc => doc.data().status === 'completed');
      const onTimeTasks = completedTasks.filter(doc => doc.data().completedOnTime);
      metrics.supplierPerformance = completedTasks.length > 0 
        ? (onTimeTasks.length / completedTasks.length) * 100
        : 0;
      
      // Documentation
      const docsSnapshot = await getDocs(
        collection(db, 'projects', projectId, 'documents')
      );
      metrics.documentationComplete = Math.min(100, docsSnapshot.size * 20);
      
      // Calculate overall score
      const weights = {
        onTimeDelivery: 0.25,
        budgetAdherence: 0.25,
        clientSatisfaction: 0.3,
        supplierPerformance: 0.15,
        documentationComplete: 0.05
      };
      
      const overallScore = Object.entries(metrics).reduce((sum, [key, value]) => 
        sum + (value * weights[key]), 0
      );
      
      return {
        overallScore: Math.round(overallScore),
        metrics,
        grade: overallScore >= 90 ? 'A' : overallScore >= 80 ? 'B' : overallScore >= 70 ? 'C' : 'D',
        recommendations: this.generateQualityRecommendations(metrics)
      };
    } catch (error) {
      console.error('Error assessing project quality:', error);
      return null;
    }
  }

  generateQualityRecommendations(metrics) {
    const recommendations = [];
    
    if (metrics.onTimeDelivery < 80) {
      recommendations.push('Improve project timeline management and buffer planning');
    }
    if (metrics.budgetAdherence < 80) {
      recommendations.push('Enhance budget tracking and cost control measures');
    }
    if (metrics.clientSatisfaction < 80) {
      recommendations.push('Increase client communication and feedback collection');
    }
    if (metrics.supplierPerformance < 80) {
      recommendations.push('Review supplier selection and performance monitoring');
    }
    if (metrics.documentationComplete < 80) {
      recommendations.push('Implement better documentation processes');
    }
    
    return recommendations;
  }

  // ============================================
  // AI API INTEGRATION
  // ============================================

  async callOpenAI(prompt, model = null) {
    try {
      if (!AI_CONFIG.openai.apiKey) {
        throw new Error('OpenAI API key not configured');
      }

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AI_CONFIG.openai.apiKey}`
        },
        body: JSON.stringify({
          model: model || AI_CONFIG.openai.model,
          messages: [
            {
              role: 'system',
              content: 'You are an expert interior design assistant for Pink Flamingo Interiors. Provide professional, detailed, and helpful responses.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 1500
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('Error calling OpenAI:', error);
      throw error;
    }
  }

  async callClaude(prompt) {
    try {
      if (!AI_CONFIG.claude.apiKey) {
        throw new Error('Claude API key not configured');
      }

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': AI_CONFIG.claude.apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: AI_CONFIG.claude.model,
          max_tokens: 1500,
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`Claude API error: ${response.status}`);
      }

      const data = await response.json();
      return data.content[0].text;
    } catch (error) {
      console.error('Error calling Claude:', error);
      throw error;
    }
  }
}

// Export singleton instance
const aiService = new AIService();
export default aiService;